// Live island presence: you see every real player online (guests too) walking, driving and waving.
// Sends your position ~5x a second; relays ride offers and invites between players.
import { signal } from "@preact/signals";
import { POS_HZ, type AvatarWire, type IslandClientMsg, type IslandServerMsg, type Mode, type Profile } from "@degen/shared/island";
import type { Engine } from "@degen/sim/engine";
import type { WorldView } from "../render/scene";
import { clientEnv } from "./env";
import { deviceId } from "./identity";
import { authToken } from "./session";
import { showToast, world } from "../ui/bridge";

/** Real players in your room (for the person card and the HUD). */
export const islandPlayers = signal<Map<string, Profile>>(new Map());
export const islandStatus = signal<"off" | "connecting" | "online">("off");
/** Someone offered you a ride / invited you over (shown as a calm prompt). */
export const rideOffer = signal<{ from: string; handle: string } | null>(null);
export const inviteOffer = signal<{ from: string; handle: string } | null>(null);

let ws: WebSocket | null = null;
let me: string | null = null;
let backoff = 1000;
let view: WorldView | null = null;
let engine: Engine | null = null;
let posTimer: ReturnType<typeof setInterval> | null = null;
let last = "";
let lastSent = 0;
const offered = new Map<string, number>(); // ride offers you sent: id -> time

const send = (m: IslandClientMsg) => {
  if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(m));
};
const handleOf = (id: string) => islandPlayers.value.get(id)?.handle ?? "someone";

function setPlayers(f: (m: Map<string, Profile>) => void) {
  const m = new Map(islandPlayers.value);
  f(m);
  islandPlayers.value = m;
}

function onMessage(msg: IslandServerMsg) {
  const v = view!;
  switch (msg.t) {
    case "welcome":
      me = msg.id;
      islandStatus.value = "online";
      backoff = 1000;
      return;
    case "join":
      if (msg.p.id === me) return;
      v.remoteJoin(msg.p);
      setPlayers((m) => m.set(msg.p.id, msg.p));
      return;
    case "leave":
      v.remoteLeave(msg.id);
      setPlayers((m) => m.delete(msg.id));
      if (rideOffer.value?.from === msg.id) rideOffer.value = null;
      if (inviteOffer.value?.from === msg.id) inviteOffer.value = null;
      return;
    case "pos":
      for (const u of msg.u) if (u[0] !== me) v.remoteMove(u);
      return;
    case "wave":
      if (msg.id === me) return;
      v.remoteWave(msg.id);
      return;
    case "ride_offer":
      rideOffer.value = { from: msg.from, handle: handleOf(msg.from) };
      return;
    case "ride_reply": {
      if (!offered.has(msg.from)) return;
      offered.delete(msg.from);
      const h = handleOf(msg.from);
      if (!msg.accept) return showToast(`@${h} said no thanks this time.`);
      const err = v.addRider(msg.from, h);
      if (err) {
        send({ t: "ride_end", to: msg.from });
        return showToast(err);
      }
      showToast(`🚗 @${h} hopped in. Drive somewhere, then drop them off for the fare.`);
      return;
    }
    case "ride_end": {
      const loc = v.player();
      if (loc.riding?.id === msg.from) {
        v.rideWith(null);
        showToast(`👋 @${handleOf(msg.from)} dropped you off.`);
      } else if (loc.riders.some((r) => r.id === msg.from)) {
        v.dropOff(msg.from); // they got out themselves
      }
      return;
    }
    case "invite":
      inviteOffer.value = { from: msg.from, handle: msg.handle };
      return;
  }
}

function sendPosition() {
  if (!view || !engine || ws?.readyState !== WebSocket.OPEN) return;
  const l = view.player();
  let mode: Mode = "walk";
  if (document.hidden) mode = "away";
  else if (l.highway) mode = "away";
  else if (l.inside) mode = "inside";
  else if (l.riding) mode = "ride";
  else if (l.driving) mode = "drive";
  const msg: IslandClientMsg = {
    t: "pos",
    x: Math.round(l.x * 100) / 100,
    z: Math.round(l.z * 100) / 100,
    dir: mode === "drive" ? Math.round(l.heading * 100) / 100 : 0,
    moving: 1,
    mode,
    ...(mode === "drive" && l.carModel ? { car: l.carModel, color: l.carColor ?? undefined } : {}),
    ...(mode === "ride" && l.riding ? { rideWith: l.riding.id } : {}),
  };
  // Only send changes (plus a keep-alive every 3 s).
  const key = JSON.stringify(msg);
  const now = performance.now();
  if (key === last && now - lastSent < 3000) return;
  last = key;
  lastSent = now;
  send(msg);
}

async function connect() {
  if (!clientEnv.apiUrl || !engine) return;
  islandStatus.value = "connecting";
  ws = new WebSocket(clientEnv.apiUrl.replace(/^http/, "ws") + "/island");
  ws.onopen = async () => {
    const p = engine!.state.player;
    send({ t: "hello", token: (await authToken()) ?? undefined, handle: p.handle, avatar: p.avatar as unknown as AvatarWire, deviceId });
    last = "";
  };
  ws.onmessage = (ev) => {
    try {
      onMessage(JSON.parse(String(ev.data)) as IslandServerMsg);
    } catch (e) {
      console.warn("[island]", e);
    }
  };
  ws.onclose = (ev) => {
    islandStatus.value = "off";
    me = null;
    view?.rideWith(null);
    for (const id of islandPlayers.value.keys()) view?.remoteLeave(id);
    islandPlayers.value = new Map();
    if (ev.code === 4003 || ev.code === 4002) return; // banned / bad data: don't hammer
    setTimeout(() => void connect(), backoff);
    backoff = Math.min(20_000, backoff * 2);
  };
}

/** Start once the 3D world exists. Single-player still works if the server is unreachable. */
export function startIsland(e: Engine, v: WorldView) {
  if (!clientEnv.apiUrl || posTimer) return;
  engine = e;
  view = v;
  void connect();
  posTimer = setInterval(sendPosition, 1000 / POS_HZ);
  document.addEventListener("visibilitychange", sendPosition);
}

// ---------------------------------------------------------------- actions for the UI
export function wave() {
  send({ t: "wave" });
  showToast("👋 You waved.");
}

export function offerRide(id: string): string | null {
  const l = view?.player();
  if (!l?.driving) return "Get in your car first.";
  if (l.highway) return "Pull off the highway first.";
  if (l.riders.length >= l.seats) return "Your seats are full.";
  const d = view!.remoteNear(id);
  if (d === null || d > 16) return "Drive up closer to them first.";
  offered.set(id, Date.now());
  send({ t: "ride_offer", to: id });
  return null;
}

export function answerRide(accept: boolean) {
  const o = rideOffer.value;
  rideOffer.value = null;
  if (!o) return;
  send({ t: "ride_reply", to: o.from, accept });
  if (accept) {
    world()?.park();
    view?.rideWith({ id: o.from, handle: o.handle });
    showToast(`🚗 You're riding with @${o.handle}. Tap "Get out" any time.`);
  }
}

/** You get out of someone's car. */
export function leaveRide() {
  const r = view?.player().riding;
  if (!r) return;
  send({ t: "ride_end", to: r.id });
  view!.rideWith(null);
}

/** You (the driver) dropped someone off: tell them. */
export function endRideFor(id: string) {
  send({ t: "ride_end", to: id });
}

export function invite(id: string) {
  send({ t: "invite", to: id });
}
